import { Select } from "components/select/index";
import { useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { ITeeTimeNew } from "redux/reducers/models/teetime";
import {
  buildGraphHourData,
  buildHourAxis,
  filterTimelineEvents,
  IDashboardEvent,
  TDashboardEventType,
} from "./dashboardEventTimeline";
import { EventBands, EventTypeLegend } from "./EventTimeline";
import "./playerBookingGraph.scss";

interface IProps {
  teetimes: Array<ITeeTimeNew>;
  courseIds: Array<number>;
  events?: Array<IDashboardEvent>;
}

const SINGLE_BAR_HEIGHT_BELOW_100 = 5;
const SINGLE_BAR_HEIGHT_ABOVE_100 = 1;
const BAR_COLOUR = "#3D5FA0";
const COMBINED_COURSE_ID = -1;

const DASHBOARD_COLOURS = [
  "#3D5FA0",
  "#1BAA5A",
  "#7357D6",
  "#F73952",
  "#C6B7F7",
  "#B846C3",
  "#E9ACE8",
  "#1B94FD",
  "#88C6F7",
  "#2CDFFD",
  "#5BCEEC",
  "#87CE9B",
  "#FEAB2D",
  "#FAC35B",
  "#FE2889",
  "#F7A8C9",
  "#F6A7A7",
];

export default function PlayerBookingGraph(props: IProps) {
  const { teetimes, courseIds, events } = props;
  const { t } = useTranslation();
  const { facilityStore } = useAppSelector(store => store);

  const availableCourses = useMemo(
    () => facilityStore?.facility?.courses?.filter(course => courseIds?.includes(course?.id)) ?? [],
    [facilityStore?.facility?.courses, courseIds],
  );

  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(courseIds?.[0] ?? null);
  const [maxBookings, setMaxBookings] = useState(0);
  const [plotAreaHeight, setPlotAreaHeight] = useState(0);
  const [singleBarHeight, setSingleBarHeight] = useState(SINGLE_BAR_HEIGHT_BELOW_100);

  const isCombined = selectedCourseId === COMBINED_COURSE_ID;
  const showCourseSelect = availableCourses.length > 1;

  useEffect(() => {
    if (!courseIds?.length) {
      setSelectedCourseId(null);
      return;
    }

    if (isCombined) {
      if (courseIds.length <= 1) {
        setSelectedCourseId(courseIds[0]);
      }
      return;
    }

    if (selectedCourseId == null || !courseIds.includes(selectedCourseId)) {
      setSelectedCourseId(courseIds[0]);
    }
  }, [courseIds, selectedCourseId, isCombined]);

  const selectedEvents = useMemo(() => {
    if (isCombined) {
      return [];
    }
    return (events ?? []).filter(event => event.course_id === selectedCourseId);
  }, [events, selectedCourseId, isCombined]);

  const timelineEvents = useMemo(() => filterTimelineEvents(selectedEvents), [selectedEvents]);
  const hourLabels = useMemo(
    () => buildHourAxis(teetimes ?? [], timelineEvents),
    [teetimes, timelineEvents],
  );

  const graphTeeTimeHours = useMemo(() => {
    if (selectedCourseId == null) {
      return [];
    }

    if (isCombined) {
      const hourData = buildGraphHourData(hourLabels, teetimes ?? [], courseIds, "course_id");
      return hourData.map(hour => ({
        hour: hour.hour,
        bookingsPerCourse: hour.bookingsPerEntity.map(entity => ({
          courseId: entity.entityId,
          bookings: entity.bookings,
        })),
      }));
    }

    const hourData = buildGraphHourData(hourLabels, teetimes ?? [], [selectedCourseId], "course_id");
    return hourData.map(hour => ({
      hour: hour.hour,
      bookingsPerCourse: [
        {
          courseId: selectedCourseId,
          bookings: hour.bookingsPerEntity[0]?.bookings ?? 0,
        },
      ],
    }));
  }, [hourLabels, teetimes, selectedCourseId, courseIds, isCombined]);

  const eventTypesInTimeline = useMemo(() => {
    const types = new Set<TDashboardEventType>();
    timelineEvents.forEach(event => types.add(event.event_type as TDashboardEventType));
    return Array.from(types);
  }, [timelineEvents]);

  useEffect(() => {
    const max = Math.max(
      ...graphTeeTimeHours.map(hourObj => hourObj.bookingsPerCourse.reduce((a, c) => a + c.bookings, 0)),
      0,
    );
    setMaxBookings(max > 0 ? Math.max(max, 25) : 25);
  }, [graphTeeTimeHours]);

  //Set y-axis scale based on maximum bookings
  useEffect(() => {
    const scaleStep = getYAxisScale(maxBookings);
    const yAxisSteps = Math.ceil(maxBookings / scaleStep) + 1;
    const barHeight = maxBookings >= 100 ? SINGLE_BAR_HEIGHT_ABOVE_100 : SINGLE_BAR_HEIGHT_BELOW_100;
    const plotHeight = (yAxisSteps + 1) * scaleStep * barHeight;
    ReactDOM.unstable_batchedUpdates(() => {
      setPlotAreaHeight(plotHeight);
      setSingleBarHeight(barHeight);
    });
  }, [maxBookings]);

  function getHourLabelText(hour: number) {
    if (hour <= 12) {
      return String(hour);
    } else if (hour === 0 || hour === 24) {
      return "12";
    } else {
      return String(hour % 12);
    }
  }

  function convertYAxisTitle(title: string) {
    const splitHours = title?.split("-");
    const finalTitle = splitHours?.map(hour => getHourLabelText(Number(hour))?.concat(":00"))?.join(" - ");
    return finalTitle;
  }

  //Display y-axis
  function yAxis() {
    const scaleStep = getYAxisScale(maxBookings);
    const yAxisSteps = Math.ceil(maxBookings / scaleStep) + 1;

    return (
      <div className="player-booking-graph-y-axis">
        <div className="player-booking-graph-y-axis-inner">
          {Array.from({ length: yAxisSteps + 1 }).map((_, i) => {
            const val = i * scaleStep;
            return (
              <div
                key={val}
                className="player-booking-graph-y-axis-row"
                style={{ height: singleBarHeight * scaleStep }}
              >
                <div className="player-booking-graph-y-axis-label">{val}</div>
                <div className="player-booking-graph-y-axis-line" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function getYAxisScale(maxValue: number) {
    if (maxValue >= 100) {
      return 20;
    }
    if (maxValue >= 10) {
      return 5;
    }
    return 5;
  }

  return (
    <div
      className="player-booking-graph-container"
      style={{
        display: !graphTeeTimeHours || graphTeeTimeHours?.length === 0 ? "none" : undefined,
      }}
    >
      {(eventTypesInTimeline.length > 0 || showCourseSelect || isCombined) && (
        <div className="player-booking-graph-legend-row">
          <div className="player-booking-graph-legend-left">
            {!isCombined ? <EventTypeLegend types={eventTypesInTimeline} /> : null}
            {isCombined ? (
              <div className="player-booking-graph-courses player-booking-graph-courses--legend">
                {availableCourses.map((course, index) => (
                  <p key={course.id} className="player-booking-graph-course">
                    <span
                      className="player-booking-graph-course-colour"
                      style={{ backgroundColor: DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length] }}
                    />{" "}
                    {course.long_name}
                  </p>
                ))}
              </div>
            ) : null}
          </div>
          {showCourseSelect ? (
            <div className="player-booking-graph-courses">
              <Select
                placeholder={t("Course")}
                onChange={(val: number) => setSelectedCourseId(val)}
                defaultValue={selectedCourseId ?? undefined}
                className="player-booking-graph-course-select-course"
              >
                {availableCourses.map(course => (
                  <Select.Option key={course.id} value={course.id} name={course.long_name}>
                    <span>{course.long_name}</span>
                  </Select.Option>
                ))}
                <Select.Option value={COMBINED_COURSE_ID} name={t("Combined")}>
                  <span>{t("Combined")}</span>
                </Select.Option>
              </Select>
            </div>
          ) : null}
        </div>
      )}
      <div className="player-booking-graph">
        {yAxis()}
        <div className="player-booking-graph-plot-area" style={{ minHeight: `${plotAreaHeight}px` }}>
          {!isCombined ? <EventBands hourLabels={hourLabels} events={selectedEvents} /> : null}
          {graphTeeTimeHours?.map(graphHour => {
            const hourTotal = graphHour.bookingsPerCourse.reduce((a, c) => a + c.bookings, 0);
            const showStackedToolTip =
              isCombined && graphHour.bookingsPerCourse.filter(course => course.bookings > 0).length > 1;
            const hideTotalToolTip = hourTotal === 0;

            return (
              <div key={graphHour.hour} className="player-booking-graph-hour">
                <OverlayTrigger
                  placement="left-end"
                  show={hideTotalToolTip ? false : undefined}
                  overlay={
                    <Tooltip id={`player-volume-bar-${graphHour?.hour}`}>
                      {`${t("Total")}`}: {hourTotal}
                    </Tooltip>
                  }
                >
                  <div className="player-booking-graph-bar-stack">
                    {graphHour.bookingsPerCourse.map((courseBooking, index) => {
                      const barHeight = courseBooking.bookings * singleBarHeight;
                      if (barHeight <= 0) {
                        return null;
                      }

                      const barColor = isCombined
                        ? DASHBOARD_COLOURS[index % DASHBOARD_COLOURS.length]
                        : BAR_COLOUR;

                      if (showStackedToolTip) {
                        return (
                          <OverlayTrigger
                            key={courseBooking.courseId}
                            placement="right"
                            overlay={
                              <Tooltip id={`player-volume-bar-${graphHour.hour}-${courseBooking.courseId}`}>
                                {courseBooking.bookings}
                              </Tooltip>
                            }
                          >
                            <div
                              className="player-booking-graph-bar"
                              style={{
                                height: `${barHeight}px`,
                                backgroundColor: barColor,
                              }}
                            />
                          </OverlayTrigger>
                        );
                      }

                      return (
                        <div
                          key={courseBooking.courseId}
                          className="player-booking-graph-bar"
                          style={{
                            height: `${barHeight}px`,
                            backgroundColor: barColor,
                          }}
                        />
                      );
                    })}
                  </div>
                </OverlayTrigger>
                <div className="player-booking-graph-hour-label">{convertYAxisTitle(graphHour.hour)}</div>
                <div className="player-booking-graph-hour-tick" />
              </div>
            );
          })}
        </div>
        {/* X-Axis Title */}
        <div className="player-booking-graph-x-axis-title">{t("Time (per hour)")}</div>
        {/* Y-Axis Title */}
        <div className="player-booking-graph-y-axis-title">{t("No. of players")}</div>
      </div>
    </div>
  );
}
